import { gatewayClient, createService } from './connectors/httpClient';
import { RawVariation } from 'packages/pages/design/VariationsContext/types';
import { envVar } from 'packages/helpers/envVar';

const httpClient = gatewayClient('/variation-sets');

const variationSetsService = createService(httpClient, (client) => ({
    /**
     * @param creativeVersionId {string|number}
     * @param params {{
     *     [sizes]: string[],
     *     [variationSets]: string[],
     *     [name]: string,
     *     [orderBy]: 'updated' | 'name',
     *     [groupBy]: 'variation' | 'size',
     *     [sortingOrder]: 'ASC' | 'DESC',
     *     [includeProperties]: boolean,
     *     [page]: number,
     *     [limit]: number,
     * }}
     *
     * @return {Promise<ResponseModel & {json: {
     *     items: Object[],
     *     totalItems: number,
     * }}>}
     */
    fetchVariationSetsByAggregate(creativeVersionId, params) {
        const searchParams = new URLSearchParams({ creativeVersionId });

        Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach((val) => {
                    searchParams.append(key, val);
                });
            } else if (key === 'groupBy' && value === 'page') {
                searchParams.append(key, 'page');
            } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                searchParams.append(key, value.toString());
            }
        });

        return client.get<{ items: RawVariation[]; totalItems: number }>(`/aggregate?${searchParams.toString()}`);
    },

    deleteVideos(ids) {
        return client.delete(`?${new URLSearchParams({ variationSetIds: ids }).toString()}`);
    },

    deleteVariationSets(ids) {
        return client.delete(`?${new URLSearchParams({ variationSetIds: ids }).toString()}`);
    },

    updateVideo(id, data) {
        return client.put(`/${id}`, data);
    },

    renameVariationSet(id, newName) {
        return client.put(`/${id}`, { name: newName });
    },

    /**
     * @param creativeId {string|number}
     * @param fileType {'CSV' | 'EXCEL'}
     * @param [variationSetIds] {string[]}
     *
     * @return {Promise<ResponseModel & {status:number}>}
     */
    batchDownload(creativeId, fileType, variationSetIds = []) {
        const searchParams = new URLSearchParams({ fileType });
        variationSetIds.forEach((variationSetId) => {
            searchParams.append('variationSetIds', variationSetId);
        });

        return client.download(`/batch/${creativeId}/download?${searchParams.toString()}`);
    },

    /**
     * @param creativeId {string|number}
     * @param data {FormData}
     *
     * @return {Promise<ResponseModel & {status:number, json: {
     *     items: Object[],
     *     totalItems: number,
     *     [error]: string,
     *     [message]: string,
     *     [appErrorCode]: number,
     * }}>}
     */
    batchUpload(creativeId, data) {
        return client.post<{ totalItems: number; items: unknown[]; appErrorCode?: number }>(
            `/batch/${creativeId}/upload`,
            data,
        );
    },

    addCreativeVariation(creativeId, name, creativeVersionPageNamedProperties = null) {
        const data: { name: string; creativeVersionPageNamedProperties?: Record<string, unknown> } = { name };

        if (creativeVersionPageNamedProperties) {
            data.creativeVersionPageNamedProperties = creativeVersionPageNamedProperties;
        }

        return client.post<RawVariation>(`/v3/${creativeId}/generate`, data);
    },

    addBatchCreativeVariations(creativeId, data) {
        return client.post<{ items: { creative: Record<string, unknown>; id: string }[] }>(
            `/v3/${creativeId}/batch-generate`,
            data,
        );
    },

    saveCreativeVariation(creativeId, variationId, name, creativeVersionPageNamedProperties) {
        return client.put<RawVariation>(`/v3/${creativeId}/variations/${variationId}`, {
            name,
            creativeVersionPageNamedProperties,
        });
    },

    saveVariationsOrdering(variations: { id: string; name: string; displayOrder: number }[]) {
        return client.put(`/batch-update`, variations);
    },

    translateVariation(
        variationId: string,
        body: { languages: string[]; translateText?: boolean; translateSubtitles?: boolean },
    ) {
        return client.post(`/${variationId}/translate`, body);
    },

    async generateSignedUrlForVariationDownload(jobId: number, jobVariationId?: number) {
        type VariationDownloadSignedUrlResponse = {
            signedUrl: string;
            expiresAt: number;
        };

        const { status, json } = await client.post<VariationDownloadSignedUrlResponse>(`/generate-signed-url`, {
            jobId,
            jobVariationId,
        });

        if (status !== 200) {
            return '';
        }

        return `${envVar.gatewayPrefix}${json.signedUrl}`;
    },
}));

export default variationSetsService;
